import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../home/components/Footer';
import SEOMeta from '../../components/SEOMeta';
import ThemeToggle from '../../components/ThemeToggle';
import { useDarkMode } from '../../hooks/useDarkMode';

const sections = [
  { id: 'sec-1', label: '1. Introdução e Identificação do Controlador' },
  { id: 'sec-2', label: '2. Dados Pessoais Coletados' },
  { id: 'sec-3', label: '3. Finalidades do Tratamento' },
  { id: 'sec-4', label: '4. Base Legal do Tratamento' },
  { id: 'sec-5', label: '5. Compartilhamento de Dados' },
  { id: 'sec-6', label: '6. Armazenamento e Segurança' },
  { id: 'sec-7', label: '7. Direitos do Titular' },
  { id: 'sec-8', label: '8. Cookies e Tecnologias' },
  { id: 'sec-9', label: '9. Retenção e Exclusão' },
  { id: 'sec-10', label: '10. Menores de Idade' },
  { id: 'sec-11', label: '11. Transferências Internacionais' },
  { id: 'sec-12', label: '12. Alterações nesta Política' },
  { id: 'sec-13', label: '13. Contato e DPO' },
];

export default function PrivacyPage() {
  const isDark = useDarkMode();
  const [activeSection, setActiveSection] = useState('sec-1');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-30% 0px -60% 0px' }
    );
    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <SEOMeta
        title="Política de Privacidade | Inspira Músicas"
        description="Saiba como a Inspira Músicas coleta, usa e protege seus dados pessoais, em conformidade com a LGPD."
        path="/politica-de-privacidade"
        noIndex={false}
      />
    <div style={{ background: 'var(--cream)', minHeight: '100vh' }}>
      {/* Header */}
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${scrolled ? 'shadow-sm' : ''}`}
        style={{ background: scrolled ? 'var(--warm-white)' : 'transparent', borderBottom: scrolled ? '1px solid var(--beige-mid)' : 'none' }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 cursor-pointer">
            <img
              src={isDark ? '/logo-dark.png' : '/logo-light.png'}
              alt="Inspira Músicas"
              className="h-9 w-auto object-contain"
            />
          </Link>
          <Link
            to="/"
            className="hidden md:flex items-center gap-2 text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap"
            style={{ color: 'var(--earth)', fontFamily: "'DM Sans', sans-serif" }}
          >
            <i className="ri-arrow-left-line text-base" />
            Voltar ao início
          </Link>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Link
              to="/#preco"
              className="px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap"
              style={{ background: 'var(--gold)', color: '#fff', fontFamily: "'DM Sans', sans-serif" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--amber-deep)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--gold)')}
            >
              Pedir Minha Música
            </Link>
          </div>
        </div>
      </header>

      {/* Hero strip */}
      <div className="pt-16" style={{ background: 'var(--beige-mid)' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
          {/* Company info */}
          <div className="mb-6 text-center">
            <p className="text-xs uppercase tracking-widest mb-1" style={{ color: 'var(--amber-deep)', fontFamily: "'DM Sans', sans-serif" }}>
              AUTOMAT TECNOLOGIA DA INFORMACAO LTDA
            </p>
            <p className="text-xs" style={{ color: 'var(--earth-light)', fontFamily: "'DM Sans', sans-serif" }}>
              CNPJ 59.640.197/0001-76 · Rua Pais Leme, 215, Conj. 1713, Sala 01, Andar 16 — Pinheiros, São Paulo/SP — CEP 05424-150
            </p>
          </div>
          <h1
            className="text-3xl md:text-5xl text-center mb-3"
            style={{ color: 'var(--heading-ink)', fontFamily: "'DM Sans', sans-serif", fontWeight: 800, letterSpacing: '-0.03em' }}
          >
            Política de Privacidade
          </h1>
          <p className="text-center text-sm italic" style={{ color: 'var(--amber-deep)', fontFamily: "'DM Sans', sans-serif" }}>
            Última atualização: 30 de março de 2026
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-14">

          {/* Sidebar TOC */}
          <aside className="hidden lg:block w-72 shrink-0">
            <div className="sticky top-24 rounded-2xl p-6" style={{ background: 'var(--warm-white)', border: '1px solid var(--beige-mid)' }}>
              <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: 'var(--amber-deep)', fontFamily: "'DM Sans', sans-serif" }}>
                Sumário
              </p>
              <ul className="flex flex-col gap-1">
                {sections.map((sec) => (
                  <li key={sec.id}>
                    <button
                      onClick={() => scrollToSection(sec.id)}
                      className="w-full text-left text-xs leading-snug py-1.5 px-2 rounded-lg transition-all duration-200 cursor-pointer"
                      style={{
                        color: activeSection === sec.id ? 'var(--gold)' : 'var(--earth-light)',
                        background: activeSection === sec.id ? 'var(--gold-pale)' : 'transparent',
                        fontFamily: "'DM Sans', sans-serif",
                        fontWeight: activeSection === sec.id ? 600 : 400,
                      }}
                    >
                      {sec.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Main text */}
          <article className="flex-1 min-w-0">
            <div className="prose-custom">

              {/* Section 1 */}
              <section id="sec-1" className="mb-10 scroll-mt-24">
                <SectionTitle number="1" title="Introdução e Identificação do Controlador" />
                <P>
                  A AUTOMAT TECNOLOGIA DA INFORMACAO LTDA, pessoa jurídica de direito privado, inscrita no CNPJ sob o nº 59.640.197/0001-76, com sede na Rua Pais Leme, 215, Conj. 1713, Sala 01, Andar 16, Pinheiros, São Paulo — SP, CEP 05424-150 ("Automat", "nós" ou "nosso"), é a empresa responsável pelo serviço Inspira Músicas, plataforma de criação de músicas personalizadas geradas por inteligência artificial e entregues via WhatsApp.
                </P>
                <P>
                  Esta Política de Privacidade ("Política") descreve como coletamos, utilizamos, armazenamos, compartilhamos e protegemos os dados pessoais dos usuários que interagem com o serviço Inspira Músicas, seja por meio do site inspiramusicais.com.br, do canal de atendimento no WhatsApp ou de qualquer outro ponto de contato disponibilizado pela Automat.
                </P>
                <P>
                  Esta Política foi elaborada em conformidade com a Lei Geral de Proteção de Dados Pessoais (Lei Federal nº 13.709/2018 — LGPD), o Marco Civil da Internet (Lei Federal nº 12.965/2014) e as demais normas aplicáveis à proteção de dados no Brasil.
                </P>
                <P>
                  Ao utilizar o serviço Inspira Músicas, você declara que leu, compreendeu e concorda com os termos desta Política. Caso não concorde com qualquer disposição aqui contida, pedimos que não utilize o serviço.
                </P>
              </section>

              <Divider />

              {/* Section 2 */}
              <section id="sec-2" className="mb-10 scroll-mt-24">
                <SectionTitle number="2" title="Dados Pessoais Coletados" />
                <P>
                  Para a prestação do serviço Inspira Músicas, coletamos diferentes categorias de dados pessoais, conforme descrito a seguir:
                </P>
                <SubTitle>2.1 Dados fornecidos diretamente pelo usuário</SubTitle>
                <P>Durante o processo de pedido e pagamento, coletamos:</P>
                <BulletList items={[
                  'Nome completo e número de telefone (WhatsApp) do contratante;',
                  'Informações sobre o destinatário da música: nome, características pessoais, memórias compartilhadas, mensagens afetivas e quaisquer outros dados fornecidos livremente pelo usuário para personalização da composição;',
                  'Preferências musicais: gênero, estilo, voz desejada;',
                  'Dados necessários para processamento do pagamento, como CPF, quando exigido pelo processador de pagamentos (PagBank);',
                  'Mensagens trocadas no canal de WhatsApp durante o atendimento.',
                ]} />
                <SubTitle>2.2 Dados coletados automaticamente</SubTitle>
                <P>Ao acessar nosso site, podemos coletar automaticamente:</P>
                <BulletList items={[
                  'Endereço IP e dados de geolocalização aproximada;',
                  'Tipo de dispositivo, sistema operacional e navegador utilizado;',
                  'Páginas visitadas, tempo de navegação e interações com o conteúdo;',
                  'Dados de cookies e tecnologias similares, conforme descrito na Seção 8.',
                ]} />
                <SubTitle>2.3 Dados sensíveis</SubTitle>
                <P>
                  Alguns dados que o usuário fornece voluntariamente para personalização da música (ex.: informações de saúde, crenças religiosas, orientação afetiva do destinatário) podem ser classificados como dados sensíveis nos termos da LGPD. Esses dados são tratados exclusivamente para a finalidade solicitada e com o consentimento expresso do usuário.
                </P>
              </section>

              <Divider />

              {/* Section 3 */}
              <section id="sec-3" className="mb-10 scroll-mt-24">
                <SectionTitle number="3" title="Finalidades do Tratamento" />
                <P>Utilizamos os dados pessoais coletados para as seguintes finalidades:</P>
                <BulletList items={[
                  'Criação e entrega da música personalizada solicitada;',
                  'Processamento do pagamento e emissão de confirmação do pedido;',
                  'Comunicação com o usuário sobre o status do pedido via WhatsApp;',
                  'Atendimento a solicitações, dúvidas, reclamações ou exercício de direitos;',
                  'Cumprimento de obrigações legais e regulatórias;',
                  'Melhoria contínua dos nossos serviços e da experiência do usuário;',
                  'Prevenção a fraudes e garantia da segurança das operações;',
                  'Envio de comunicações sobre novidades e promoções, quando o usuário consentir expressamente.',
                ]} />
              </section>

              <Divider />

              {/* Section 4 */}
              <section id="sec-4" className="mb-10 scroll-mt-24">
                <SectionTitle number="4" title="Base Legal do Tratamento" />
                <P>
                  O tratamento de dados pessoais pela Automat está fundamentado nas seguintes bases legais previstas no art. 7º da LGPD:
                </P>
                <BulletList items={[
                  'Execução de contrato (art. 7º, V): para criar e entregar a música personalizada contratada;',
                  'Cumprimento de obrigação legal (art. 7º, II): para atender exigências fiscais, contábeis e regulatórias;',
                  'Legítimo interesse (art. 7º, IX): para prevenção a fraudes e melhoria dos serviços;',
                  'Consentimento (art. 7º, I): para envio de comunicações de marketing e tratamento de dados sensíveis, quando aplicável.',
                ]} />
              </section>

              <Divider />

              {/* Section 5 */}
              <section id="sec-5" className="mb-10 scroll-mt-24">
                <SectionTitle number="5" title="Compartilhamento de Dados" />
                <P>
                  A Automat não vende, aluga ou cede dados pessoais a terceiros para fins comerciais. O compartilhamento de dados ocorre apenas nas situações descritas abaixo:
                </P>
                <BulletList items={[
                  'Processador de pagamentos (PagBank): para viabilizar a cobrança e o processamento seguro das transações financeiras;',
                  'Ferramentas de IA para geração musical: os dados de personalização são enviados às APIs de inteligência artificial utilizadas exclusivamente para compor a música solicitada;',
                  'Plataforma WhatsApp (Meta): utilizada para entrega da música e comunicação com o cliente;',
                  'Parceiros de análise e publicidade (Google Analytics, Meta Pixel): para métricas de desempenho, com dados anonimizados sempre que possível;',
                  'Autoridades competentes: quando exigido por lei, decisão judicial ou regulação aplicável.',
                ]} />
                <P>
                  Todos os parceiros que recebem dados pessoais estão sujeitos a obrigações contratuais de confidencialidade e segurança compatíveis com a LGPD.
                </P>
              </section>

              <Divider />

              {/* Section 6 */}
              <section id="sec-6" className="mb-10 scroll-mt-24">
                <SectionTitle number="6" title="Armazenamento e Segurança" />
                <P>
                  Os dados pessoais são armazenados em servidores seguros, com controles técnicos e organizacionais adequados para protegê-los contra acesso não autorizado, perda, alteração ou divulgação indevida. Adotamos as seguintes medidas de segurança:
                </P>
                <BulletList items={[
                  'Criptografia de dados em trânsito (TLS/SSL) e em repouso;',
                  'Controle de acesso com autenticação de múltiplos fatores para sistemas internos;',
                  'Monitoramento contínuo de ameaças e vulnerabilidades;',
                  'Procedimentos documentados de resposta a incidentes de segurança.',
                ]} />
                <P>
                  Em caso de incidente de segurança que possa acarretar risco relevante aos titulares, a Automat notificará a Autoridade Nacional de Proteção de Dados (ANPD) e os usuários afetados dentro dos prazos previstos na LGPD.
                </P>
              </section>

              <Divider />

              {/* Section 7 */}
              <section id="sec-7" className="mb-10 scroll-mt-24">
                <SectionTitle number="7" title="Direitos do Titular" />
                <P>
                  Nos termos da LGPD (art. 18), você possui os seguintes direitos em relação aos seus dados pessoais:
                </P>
                <BulletList items={[
                  'Confirmação da existência de tratamento e acesso aos dados;',
                  'Correção de dados incompletos, inexatos ou desatualizados;',
                  'Anonimização, bloqueio ou eliminação de dados desnecessários ou tratados em desconformidade;',
                  'Portabilidade dos dados a outro fornecedor de serviço;',
                  'Eliminação dos dados tratados com base no consentimento;',
                  'Informação sobre as entidades com as quais os dados são compartilhados;',
                  'Informação sobre a possibilidade de não fornecer consentimento e as consequências disso;',
                  'Revogação do consentimento a qualquer momento, sem prejuízo das operações realizadas antes da revogação.',
                ]} />
                <P>
                  Para exercer qualquer desses direitos, entre em contato conosco pelo e-mail privacidade@inspiramusicais.com.br ou pelo WhatsApp de atendimento. Responderemos à sua solicitação no prazo de até 15 dias úteis.
                </P>
              </section>

              <Divider />

              {/* Section 8 */}
              <section id="sec-8" className="mb-10 scroll-mt-24">
                <SectionTitle number="8" title="Cookies e Tecnologias de Rastreamento" />
                <P>
                  Nosso site utiliza cookies e tecnologias similares para melhorar a experiência de navegação, analisar o desempenho e exibir anúncios personalizados. Os tipos de cookies utilizados são:
                </P>
                <BulletList items={[
                  'Cookies essenciais: necessários para o funcionamento básico do site;',
                  'Cookies de desempenho: coletam informações anônimas sobre como os usuários utilizam o site (ex.: Google Analytics);',
                  'Cookies de marketing: utilizados para exibir anúncios relevantes em outras plataformas (ex.: Meta Pixel).',
                ]} />
                <P>
                  Você pode gerenciar ou desativar os cookies nas configurações do seu navegador. A desativação de cookies essenciais pode comprometer a funcionalidade do site.
                </P>
              </section>

              <Divider />

              {/* Section 9 */}
              <section id="sec-9" className="mb-10 scroll-mt-24">
                <SectionTitle number="9" title="Retenção e Exclusão de Dados" />
                <P>
                  Mantemos os dados pessoais pelo período necessário ao cumprimento das finalidades para as quais foram coletados, respeitando os seguintes critérios:
                </P>
                <BulletList items={[
                  'Dados de pedidos e pagamentos: mantidos por 5 (cinco) anos para fins fiscais e contábeis, conforme legislação brasileira;',
                  'Dados de atendimento via WhatsApp: mantidos por 1 (um) ano após o encerramento do atendimento;',
                  'Dados de navegação e cookies: mantidos por até 13 (treze) meses, conforme recomendação da ANPD;',
                  'Dados fornecidos para personalização da música: excluídos ou anonimizados após a entrega da composição, salvo solicitação de revisão dentro do prazo de garantia.',
                ]} />
                <P>
                  Após o término do prazo de retenção, os dados serão excluídos de forma segura ou anonimizados, de modo que não seja possível associá-los a um titular específico.
                </P>
              </section>

              <Divider />

              {/* Section 10 */}
              <section id="sec-10" className="mb-10 scroll-mt-24">
                <SectionTitle number="10" title="Menores de Idade" />
                <P>
                  O serviço Inspira Músicas é destinado a pessoas com 18 anos ou mais. Não coletamos intencionalmente dados pessoais de menores de 18 anos. Caso identifiquemos que dados de menores foram coletados sem o devido consentimento parental, tomaremos providências imediatas para excluí-los.
                </P>
                <P>
                  Se você é responsável legal por um menor e acredita que seus dados foram coletados inadvertidamente, entre em contato conosco pelo e-mail privacidade@inspiramusicais.com.br.
                </P>
              </section>

              <Divider />

              {/* Section 11 */}
              <section id="sec-11" className="mb-10 scroll-mt-24">
                <SectionTitle number="11" title="Transferências Internacionais de Dados" />
                <P>
                  O serviço Inspira Músicas pode envolver transferências de dados pessoais para servidores e sistemas localizados fora do Brasil, especialmente para as plataformas de inteligência artificial utilizadas na composição musical e para os provedores de infraestrutura de nuvem.
                </P>
                <P>
                  Essas transferências são realizadas em conformidade com o art. 33 da LGPD, garantindo que os destinatários ofereçam nível adequado de proteção de dados ou que existam cláusulas contratuais específicas assegurando a proteção dos seus direitos.
                </P>
              </section>

              <Divider />

              {/* Section 12 */}
              <section id="sec-12" className="mb-10 scroll-mt-24">
                <SectionTitle number="12" title="Alterações nesta Política" />
                <P>
                  A Automat reserva-se o direito de atualizar esta Política periodicamente, para refletir mudanças em nossas práticas, obrigações legais ou melhorias no serviço. A versão mais atualizada estará sempre disponível em nosso site com indicação da data da última revisão.
                </P>
                <P>
                  Alterações significativas serão comunicadas diretamente aos usuários por meio do WhatsApp de atendimento ou por e-mail, quando aplicável. O uso continuado do serviço após a divulgação das alterações implica a aceitação da versão atualizada.
                </P>
              </section>

              <Divider />

              {/* Section 13 */}
              <section id="sec-13" className="mb-10 scroll-mt-24">
                <SectionTitle number="13" title="Contato e Encarregado de Dados (DPO)" />
                <P>
                  Para exercer seus direitos, esclarecer dúvidas ou registrar reclamações relacionadas a esta Política ou ao tratamento de seus dados pessoais, entre em contato com nosso Encarregado de Proteção de Dados (DPO):
                </P>
                <div className="rounded-2xl p-6 mt-4" style={{ background: 'var(--gold-pale)', border: '1px solid var(--gold-border)' }}>
                  <p className="text-sm font-semibold mb-3" style={{ color: 'var(--earth)', fontFamily: "'DM Sans', sans-serif" }}>
                    AUTOMAT TECNOLOGIA DA INFORMACAO LTDA
                  </p>
                  <div className="flex flex-col gap-2">
                    {[
                      { icon: 'ri-map-pin-line', text: 'Rua Pais Leme, 215, Conj. 1713, Sala 01, Andar 16 — Pinheiros, São Paulo/SP — CEP 05424-150' },
                      { icon: 'ri-mail-line', text: 'privacidade@inspiramusicais.com.br' },
                      { icon: 'ri-whatsapp-line', text: 'Canal de atendimento WhatsApp (disponível no site)' },
                      { icon: 'ri-global-line', text: 'inspiramusicais.com.br' },
                    ].map((item) => (
                      <div key={item.icon} className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                          <i className={`${item.icon} text-sm`} style={{ color: 'var(--gold)' }} />
                        </div>
                        <p className="text-sm" style={{ color: 'var(--earth-light)', fontFamily: "'DM Sans', sans-serif" }}>
                          {item.text}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
                <P>
                  Também é possível registrar reclamações perante a Autoridade Nacional de Proteção de Dados (ANPD), por meio do site <strong>gov.br/anpd</strong>, caso considere que seus direitos não foram atendidos adequadamente.
                </P>
              </section>

            </div>

            {/* Back to top */}
            <div className="mt-12 text-center">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap"
                style={{ background: 'var(--beige-mid)', color: 'var(--earth)', fontFamily: "'DM Sans', sans-serif" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--beige-footer)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--beige-mid)')}
              >
                <i className="ri-arrow-up-line text-sm" />
                Voltar ao topo
              </button>
            </div>
          </article>

        </div>
      </div>

      <Footer />
    </div>
    </>
  );
}

/* ---------- helpers ---------- */

function SectionTitle({ number, title }: { number: string; title: string }) {
  return (
    <div className="flex items-start gap-3 mb-4">
      <span
        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mt-0.5"
        style={{ background: 'var(--gold-pale)', color: 'var(--gold)', fontFamily: "'DM Sans', sans-serif" }}
      >
        {number}
      </span>
      <h2
        className="text-xl md:text-2xl leading-snug"
        style={{ color: 'var(--heading-ink)', fontFamily: "'DM Sans', sans-serif", fontWeight: 800, letterSpacing: '-0.03em' }}
      >
        {title}
      </h2>
    </div>
  );
}

function SubTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3
      className="text-base font-semibold mt-5 mb-2"
      style={{ color: 'var(--earth)', fontFamily: "'DM Sans', sans-serif" }}
    >
      {children}
    </h3>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="text-sm leading-relaxed mb-3"
      style={{ color: 'var(--text-strong)', fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
    >
      {children}
    </p>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="flex flex-col gap-2 my-3 ml-2">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3">
          <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: 'var(--gold)' }} />
          <span
            className="text-sm leading-relaxed"
            style={{ color: 'var(--text-strong)', fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
          >
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

function Divider() {
  return <div className="h-px my-8" style={{ background: 'var(--beige-mid)' }} />;
}
